import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Calendar, MapPin, Users, Briefcase, Clock, ArrowRight, Star, TrendingUp, Award, Heart, ChevronDown, ChevronUp, Bell, Mail, Sparkles, Coffee, Car } from 'lucide-react';
import { useState, useEffect } from 'react';
import { db } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface JobPosition {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'full-time' | 'part-time' | 'contract';
  experience: string;
  salary: string;
  description: string;
  job_description: string[];
  requirements: string[];
  benefits: string[];
  posted_date: string;
  is_active: boolean;
}

const Careers = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [positions, setPositions] = useState<JobPosition[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [funFactIndex, setFunFactIndex] = useState(0);

  useEffect(() => {
    loadPositions();
  }, []);

  // Rotate fun facts every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFunFactIndex((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    try {
      const result = await db.jobNotifications.subscribe(email);
      if (result.success) {
        toast({
          title: t('careers.empty.success'),
          description: t('careers.empty.success.description'),
        });
        setEmail('');
      } else {
        toast({
          title: t('careers.empty.already.subscribed'),
          description: 'You are already subscribed for job notifications.',
          variant: 'default'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to subscribe. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadPositions = async () => {
    try {
      const data = await db.jobs.getActive();
      setPositions(data);
    } catch (error) {
      console.error('Error loading positions:', error);
      // Fallback to mock data if Supabase is not configured
      setPositions([]);
    }
  };

  const departments = ['all', 'Výroba', 'Kvalita', 'Logistika', 'Administratíva', 'IT'];
  const jobTypes = ['all', 'full-time', 'part-time', 'contract'];

  const filteredPositions = positions.filter(position => {
    const matchesDepartment = selectedDepartment === 'all' || position.department === selectedDepartment;
    const matchesType = selectedType === 'all' || position.type === selectedType;
    return matchesDepartment && matchesType && position.is_active;
  });

  const benefits = [
    { icon: TrendingUp, title: 'Kariérny rast', description: 'Jasné možnosti postupu' },
    { icon: Award, title: 'Konkurenčné platy', description: 'Nadštandardné ohodnotenie' },
    { icon: Heart, title: 'Benefity', description: 'Komplexný balík benefitov' },
    { icon: Users, title: 'Tímová práca', description: 'Príjemné pracovné prostredie' }
  ];

  const funFacts = [
    { icon: Sparkles, text: t('careers.empty.fun.fact1') },
    { icon: Coffee, text: t('careers.empty.fun.fact2') },
    { icon: Car, text: t('careers.empty.fun.fact3') }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section id="careers" className="py-20 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-slate-800 via-slate-600 to-slate-800 bg-clip-text text-transparent mb-6"
          >
            Staňte sa súčasťou nášho tímu
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed"
          >
            Hľadáme talentovaných ľudí, ktorí sa chcú stať súčasťou nášho úspešného tímu. 
            Ponúkame zaujímavé pracovné príležitosti v dynamickom prostredí automobilového priemyslu.
          </motion.p>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-2xl p-6 text-center hover:border-slate-300 transition-all duration-300 hover-lift shadow-lg"
            >
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white mb-4">
                <benefit.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Detailed Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-2xl p-8 md:p-12 shadow-lg">
            <h3 className="text-3xl md:text-4xl font-bold text-slate-800 text-center mb-8">
              PREČO SA OPLATÍ BYŤ SÚČASŤOU NÁŠHO TÍMU ✨
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Multisport karta</h4>
                    <p className="text-gray-600 text-sm">Možnosť získať Multisport kartu, vďaka ktorej si môžeš vybrať z množstva športových a relaxačných aktivít</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">IAC Cafeteria systém</h4>
                    <p className="text-gray-600 text-sm">Vďaka IAC Cafeteria systému si sám vyberieš, na čo chceš príspevok – či už na šport, vzdelávanie, zdravie alebo voľný čas</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Pravidelné masáže</h4>
                    <p className="text-gray-600 text-sm">Dopraj si chvíľu oddychu priamo na pracovisku – pravidelné masáže ti pomôžu načerpať novú energiu</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Príspevok na dopravu</h4>
                    <p className="text-gray-600 text-sm">Samozrejme nechceme, aby si mal vysoké náklady na cestovanie do práce, prispejeme ti</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Závodná jedáleň</h4>
                    <p className="text-gray-600 text-sm">U nás hladný nezostaneš, máme pre teba závodnú jedáleň, kde ti taktiež prispejeme</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Letný a zimný bonus</h4>
                    <p className="text-gray-600 text-sm">Tvoj pracovný výkon môže dopomôcť k tomu, aby firma dosiahla výsledky, ktoré môžu byť odmenené bonusom</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-slate-800">5 dní dovolenky navyše</h4>
                    <p className="text-gray-600 text-sm">K tomu všetkému dostaneš 5 dní dovolenky navyše</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <h4 className="text-2xl font-bold text-slate-800 mb-4">
                ZAUJALO ŤA TO?
              </h4>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Neváhaj a pošli nám svoj životopis! Tešíme sa, že práve TY môžeš byť tou chýbajúcou časťou skladačky v našom tíme!
              </p>
              <p className="text-lg font-semibold text-slate-800 mb-6">
                IAC Group - Tvoríme budúcnosť automotive spolu s najlepšími ľuďmi. Buď jedným z nich!
              </p>
              <Button 
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-lg px-8 py-3"
                onClick={() => window.open('mailto:hr@iac-slovakia.sk?subject=Prihláška do tímu IAC Group', '_blank')}
              >
                Poslať životopis teraz
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12 space-y-6"
        >
          <div className="flex flex-wrap justify-center gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Oddelenie</label>
              <div className="flex flex-wrap gap-2">
                {departments.map((dept) => (
                  <Button
                    key={dept}
                    onClick={() => setSelectedDepartment(dept)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                      selectedDepartment === dept
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : 'bg-white text-gray-700 border border-slate-300 hover:bg-slate-100 shadow-sm'
                    }`}
                  >
                    {dept === 'all' ? 'Všetky' : dept}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Typ pracovného pomeru</label>
              <div className="flex flex-wrap gap-2">
                {jobTypes.map((type) => (
                  <Button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                      selectedType === type
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : 'bg-white text-gray-700 border border-slate-300 hover:bg-slate-100 shadow-sm'
                    }`}
                  >
                    {type === 'all' ? 'Všetky' : type === 'full-time' ? 'Plný úväzok' : type === 'part-time' ? 'Polovičný úväzok' : 'Zmluva'}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Job Positions */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-6 lg:grid-cols-2"
          key={`${selectedDepartment}-${selectedType}`}
        >
          {filteredPositions.map((position) => (
            <motion.div
              key={position.id}
              variants={itemVariants}
              className="bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-2xl shadow-lg overflow-hidden hover:border-slate-300 transition-all duration-300"
            >
              {/* Collapsed view - always visible */}
              <div 
                className="p-6 cursor-pointer hover:bg-white/50 transition-colors duration-200"
                onClick={() => setExpandedId(expandedId === position.id ? null : position.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-1">
                          {position.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {position.department} • {position.location}
                        </p>
                      </div>
                      <Badge 
                        className={`ml-4 ${
                          position.type === 'full-time' ? 'bg-green-100 text-green-800' :
                          position.type === 'part-time' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {position.type === 'full-time' ? 'Plný úväzok' :
                         position.type === 'part-time' ? 'Polovičný úväzok' : 'Zmluva'}
                      </Badge>
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {position.salary}
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <motion.div
                      animate={{ rotate: expandedId === position.id ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Expanded content with animation */}
              <AnimatePresence>
                {expandedId === position.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-gray-700 mb-6">{position.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                              <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                              Náplň práce
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-2">
                              {(position.job_description || []).map((desc, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-blue-600 mr-2 mt-1">•</span>
                                  <span>{desc}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center">
                              <Users className="w-4 h-4 mr-2 text-green-600" />
                              Požiadavky
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-2">
                              {position.requirements.map((req, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-green-600 mr-2 mt-1">•</span>
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Skúsenosť: {position.experience}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            Pridané: {new Date(position.posted_date).toLocaleDateString('sk-SK')}
                          </div>
                        </div>
                        
                        <Button 
                          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`mailto:hr@iac-slovakia.sk?subject=Prihláška na pozíciu: ${position.title}`, '_blank');
                          }}
                        >
                          Prihlásiť sa
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {filteredPositions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="inline-block"
            >
              <Briefcase className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              {t('careers.empty.title')}
            </h3>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('careers.empty.description')}
            </p>

            {/* Fun fact rotation */}
            <motion.div
              key={funFactIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 mb-8 max-w-md mx-auto border border-slate-200"
            >
              <div className="flex items-center justify-center mb-3">
                {(() => {
                  const IconComponent = funFacts[funFactIndex].icon;
                  return <IconComponent className="w-8 h-8 text-purple-500 mr-3" />;
                })()}
                <span className="text-sm font-semibold text-purple-600">Zaujímavosť</span>
              </div>
              <p className="text-gray-700 text-sm">{funFacts[funFactIndex].text}</p>
            </motion.div>

            {/* Email subscription form */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 max-w-md mx-auto border border-blue-200">
              <div className="flex items-center justify-center mb-4">
                <Bell className="w-6 h-6 text-blue-600 mr-2" />
                <h4 className="text-xl font-bold text-slate-800">{t('careers.empty.subscribe')}</h4>
              </div>
              
              <form onSubmit={handleSubscribe} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder={t('careers.empty.email.placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Odosiela sa...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      {t('careers.empty.submit')}
                    </>
                  )}
                </Button>
              </form>
              
              <p className="text-xs text-gray-500 mt-4">
                Odberom súhlasíte s tým, že vás budeme kontaktovať keď sa objavia nové pracovné príležitosti.
                Z odberu sa môžete kedykoľvek odhlásiť.
              </p>
            </div>

            {/* Alternative CTA */}
            <div className="mt-8">
              <p className="text-gray-600 mb-4">Alebo nám rovno pošlite životopis</p>
              <Button 
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => window.open('mailto:lukas.panis@iacgroup.com', '_blank')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Poslať životopis
              </Button>
            </div>
          </motion.div>
        )}

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Card className="bg-white/80 backdrop-blur-sm border-slate-200/80 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                Máš otázky?
              </h3>
              <p className="text-gray-600 mb-6">
                Neváhajte nás kontaktovať ohľadom pracovných príležitostí alebo pošlite nám svoj životopis.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                  onClick={() => window.open('mailto:lukas.panis@iacgroup.com', '_blank')}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Kontaktovať HR
                </Button>
                <Button 
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  onClick={() => window.open('tel:+421123456789', '_blank')}
                >
                  Zavolať
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Careers;